import type { UIColorTokens } from "@/core/constants/uiColors";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import LogoSvg from "@assets/images/bloom-logo/logo.svg";
import { Image } from "expo-image";
import React from "react";
import { Platform, StyleProp, View, ViewStyle } from "react-native";

const ArabicLogo = require("@assets/images/bloom-logo/Arabic.png");

// logo.svg's paths hardcode fill="#922A22" (ignores the `color` prop entirely),
// so the English wordmark always renders this red regardless of what's passed.
// Match it here so Arabic looks the same instead of respecting `color`.
const ENGLISH_SVG_RED = "#922A22";

export interface LogoProps {
  color?: keyof UIColorTokens;
  size?: "small" | "medium" | "large-medium" | "large" | "xlarge";
  style?: StyleProp<ViewStyle>;
}

export const Logo = React.memo<LogoProps>(function Logo({
  color = "bloomRed",
  size = "medium",
  style,
}) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const getLogoColor = () => {
    return colors[color] || colors.bloomRed;
  };

  // New logo aspect ratio: 1727:646 ≈ 2.67:1
  const getSize = () => {
    switch (size) {
      case "small":
        return { height: 24, width: 64 }; // ~2.67:1 ratio
      case "medium":
        return { height: 32, width: 86 }; // ~2.67:1 ratio
      case "large-medium":
        return { height: 64, width: 140 }; // Figma header size: 140px × 64px
      case "large":
        return { height: 48, width: 128 }; // ~2.67:1 ratio
      case "xlarge":
        return { height: 64, width: 172 }; // larger hero usage
      default:
        return { height: 32, width: 86 };
    }
  };

  const dimensions = getSize();
  const fillColor = getLogoColor();

  // Use Arabic PNG logo for RTL, SVG for LTR
  if (isRTL) {
    const imageStyle: any = {
      width: dimensions.width,
      height: dimensions.height,
    };
    // expo-image tints via a dedicated `tintColor` prop, not a style key.
    let nativeTintColor: string | null = null;

    if (Platform.OS === "web") {
      // Web unchanged: CSS filter only handles the white case.
      if (color === "white") {
        // Use CSS filter to convert image to white
        // brightness(0) makes it black, then invert() makes it white
        imageStyle.filter = "brightness(0) invert(1)";
      }
    } else {
      // Mobile: tint to the same red the English SVG actually shows, not the
      // requested `color` — keeps both languages visually consistent.
      nativeTintColor = ENGLISH_SVG_RED;
    }

    return (
      <View style={[dimensions, style]}>
        <Image
          source={ArabicLogo}
          style={imageStyle}
          contentFit="contain"
          tintColor={nativeTintColor}
        />
      </View>
    );
  }

  return (
    <View style={[dimensions, style]}>
      <LogoSvg
        width={dimensions.width}
        height={dimensions.height}
        color={fillColor}
      />
    </View>
  );
});

export default Logo;
